/**
 * Internal App-Config Logos Maintenance Contract
 *
 * POST /internal/config/app-config/strip-default-logos
 *
 * API-key-gated (`INTERNAL_API_KEY`). A one-purpose data-repair endpoint, not a
 * general config writer: it removes the `assets.logos` key from the FLEET-WIDE
 * default app-config row (`SystemConfiguration` where `school_id IS NULL`,
 * `MOBILE_CONFIG` / `APP_CONFIG`) and nothing else.
 *
 * WHY IT EXISTS. That row carried a `logos` block in the LEGACY FLAT shape
 * (`{ 'small-light', 'small-dark', 'large-light', 'large-dark' }`) whose four
 * URLs were all one generic School Journey placeholder — not any school's
 * artwork. `getConfiguration` merges the fleet-wide row UNDERNEATH every
 * school's own row, so every school inherited that block, and mobile (which
 * reads the NESTED `logos.small.light` shape) could not use it and fell back to
 * an initials placeholder. The READ side was fixed separately: school-owned
 * artwork now outranks the inherited flat block. This endpoint is the DATA half
 * — it deletes the junk so nothing keeps handing schools a placeholder.
 *
 * IDEMPOTENT. A second call finds no `assets.logos`, reports `changed: false`,
 * and returns 200. Only `assets.logos` is touched; `assets.backgrounds`, every
 * other key under `assets`, and the whole rest of the config are preserved
 * byte-for-byte.
 *
 * AUDIT. The write records `{ reason, changedBy, changedAt }` in the row's
 * `metadata` column — the same audit shape and field bag
 * (`SystemFlagAuditRequestFields`) that `POST /internal/config/flags/:name`
 * uses, deliberately reused rather than forked so every internal config write
 * reads back the same way.
 */

import { z } from 'zod'
import { SystemFlagAuditRequestFields } from '../../schemas/entities/system-flag'

/**
 * The one placeholder URL that makes a flat `logos` block strippable from a
 * PER-SCHOOL row.
 *
 * THIS EXACT-EQUALITY CHECK IS A SAFETY BOUNDARY, NOT A HEURISTIC. Three schools
 * hold flat `logos` blocks pointing at their OWN real logos, and at least one of
 * them has no `School.logoUrl*` columns at all — its flat block is the ONLY
 * source of its branding. A looser rule (matching on the Cloudinary host, or on
 * the flat key shape alone) would blank a real school's logo. Every URL value in
 * the block must equal this string for the block to be considered placeholder
 * junk; one differing value disqualifies the whole block.
 */
export const DEFAULT_APP_CONFIG_PLACEHOLDER_LOGO_URL =
  'https://res.cloudinary.com/dcwizt2yq/image/upload/v1761015628/logo-small-light_b2hujs.png'

/**
 * POST /internal/config/app-config/strip-default-logos — request body.
 *
 * Every field is optional; an empty body performs a live strip of the
 * fleet-wide row alone, which is the intended default operation.
 */
export const StripDefaultLogosRequestSchema = z.object({
  dry_run: z.boolean().default(false).describe('Compute and report exactly what WOULD be removed, writing nothing'),
  include_placeholder_school_rows: z
    .boolean()
    .default(false)
    .describe(
      'Also strip per-school MOBILE_CONFIG/APP_CONFIG rows, but ONLY where the logos block is flat and EVERY ' +
        'URL in it equals the known placeholder. Schools whose flat block holds their own real logo are never touched.'
    ),
  ...SystemFlagAuditRequestFields
})

export type StripDefaultLogosRequest = z.infer<typeof StripDefaultLogosRequestSchema>

/**
 * What happened to the fleet-wide default row.
 *
 * `removedLogos` is the block as it stood before the strip — the operator's
 * record of what was deleted — and is `null` whenever nothing was removed.
 */
export const StripDefaultLogosDefaultRowSchema = z.object({
  id: z.number().describe('`SystemConfiguration.id` of the fleet-wide default row that was inspected'),
  changed: z
    .boolean()
    .describe('Did this call remove a logos block? False on an already-clean row, and always false under dry_run'),
  removedLogos: z
    .unknown()
    .nullable()
    .describe('The `assets.logos` block that was (or, under dry_run, would be) removed; null when there was none')
})

/**
 * What happened to one per-school row. Only ever populated when
 * `include_placeholder_school_rows` was requested.
 */
export const StripDefaultLogosSchoolRowSchema = z.object({
  schoolId: z.string().describe("School's public ID (CUID)"),
  schoolName: z.string().describe("School's name, so the operator can read the audit without a second lookup"),
  changed: z.boolean().describe('Did this call remove the placeholder logos block from this school row?'),
  removedLogos: z
    .unknown()
    .nullable()
    .describe('The placeholder block that was (or, under dry_run, would be) removed; null when there was none')
})

/**
 * POST /internal/config/app-config/strip-default-logos — response.
 *
 * Reports every row inspected and changed, so the operator can see the exact
 * blast radius rather than a bare success message.
 */
export const StripDefaultLogosResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    dryRun: z.boolean().describe('Was this a dry run? When true nothing was written'),
    defaultRow: StripDefaultLogosDefaultRowSchema,
    schoolRows: z
      .array(StripDefaultLogosSchoolRowSchema)
      .describe(
        'Per-school rows that carried a placeholder block, empty unless include_placeholder_school_rows was set. ' +
          'Rows whose logos block is not placeholder junk are absent entirely — they were never candidates.'
      )
  })
})

export type StripDefaultLogosResponse = z.infer<typeof StripDefaultLogosResponseSchema>
